import { BrowserModule } from '@angular/platform-browser';
import { LocationStrategy, HashLocationStrategy } from '@angular/common';
import { HttpClientModule, provideHttpClient, withInterceptors } from '@angular/common/http';
import { NgModule, ErrorHandler, inject, provideAppInitializer } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { environment } from '../environments/environment';
import { RouterModule, PreloadAllModules } from '@angular/router';
import { TeamChatStore, StoresModule, TextChatStore } from '@stores';
import { AppointmentsService, GlobalsService, LookupService, LoginService, NotificationSettingsService, TasksService, HttpService, AuthService, NotificationSoundService, QuickbooksService, FunctionLockService, PriceBooksService, SleepService } from '@services';
import { ZiftService } from '@services/credit-cards/zift.service';
import { HttpErrorDialogComponent } from './http-error-dialog.component';
import { CustomErrorDialogComponent } from './custom-error-dialog.component';
import { VersionReleaseDialogModule } from '@shared-components/version-release-dialog';
import { AnnouncementDialogModule } from '@shared-components/announcement-dialog';
import { OnboardingSetupSharedModule } from './onboarding-setup/onboarding-setup-shared.module';
import { FeaturePopupModule } from '@shared-components/feature-popup';
import { SlickButtonSpinnerModule, SlickDialogModule, SlickPopoverModule, SlickInitService, SlickInitParams } from 'slick-components';
import { OfflineModule, OfflineStore } from '@offline';
import { NgxConfettiExplosionModule } from '@shared-components/ngx-confetti-explosion';

import { AppComponent } from './app.component';
import { AppRoutes } from './routing';

// Services
import { InactivityTimeoutService } from '@services/inactivity-timeout.service';
import { ThemeService } from '@components/theme.service';
// Landing
import { LandingComponent } from './landing/landing.component';
import { PageNotFoundComponent } from './page-not-found/page-not-found.component';
// Layout
import { LayoutModule } from './layout/layout.module';
import { TeamChatWidgetModule } from './team-chat/team-chat-widget';
import { ThemeSwitcherModule } from './theme-switcher/theme-switcher.module';
import { TextChatHubService } from '../../../libraries/signalr/text-chat-hub.service';
import { MessageQueueDispatcher, MessageQueueHubService } from '@message-queue';
import { AzureBlobStorageService } from '@services/azure-blob-storage.service';
import { DocumentsService } from '@services/documents.service';
import { NgxEchartsModule } from 'ngx-echarts';
import * as echarts from 'echarts';
import { jobsightInterceptor } from './http.interceptor';

@NgModule({
	declarations: [AppComponent, LandingComponent, PageNotFoundComponent, HttpErrorDialogComponent, CustomErrorDialogComponent],
	imports: [
		BrowserModule,
		FormsModule,
		LayoutModule,
		TeamChatWidgetModule,
		ThemeSwitcherModule,
		StoresModule,
		OfflineModule,
		VersionReleaseDialogModule,
		AnnouncementDialogModule,
		FeaturePopupModule,
		OnboardingSetupSharedModule,
		NgxConfettiExplosionModule,
		SlickButtonSpinnerModule,
		SlickDialogModule,
		SlickPopoverModule,
		NgxEchartsModule.forRoot({ echarts }),
		RouterModule.forRoot(AppRoutes, { preloadingStrategy: PreloadAllModules }),
	],
	providers: [
		// Needed at the root because root-provided stores inject them - TextChatStore takes
		// AppointmentsService.  (The reminder itself no longer fetches appointments; RemindersStore asks
		// the server for the next one - see StoresModule, which provides what that needs.)
		AppointmentsService,
		NotificationSettingsService,
		AzureBlobStorageService,
		DocumentsService,
		InactivityTimeoutService,
		LoginService,
		TasksService,
		AuthService,
		HttpService,
		LookupService,
		FunctionLockService,
		QuickbooksService,
		ZiftService,
		GlobalsService,
		ThemeService,
		MessageQueueHubService,
		TextChatHubService,
		MessageQueueDispatcher,
		PriceBooksService,
		provideHttpClient(withInterceptors([jobsightInterceptor])),
		provideAppInitializer(init),
		{
			provide: LocationStrategy,
			useClass: HashLocationStrategy,
		},
	],
	bootstrap: [AppComponent],
})
export class AppModule { }

// Origin of the dispatch or corporate site for the environment this build targets, e.g.
// https://stagingcorporate.jobsight.com. Hotfix and learning paths have no sites of their own, so
// they fall through to production.
function getSubAppUrl(subApp: 'dispatch' | 'corporate'): string {
	if (GlobalsService.isStaging) return `https://staging${subApp}.jobsight.com`;
	if (GlobalsService.isRC) return `https://rc${subApp}.jobsight.com`;
	if (GlobalsService.isDebug) return `http://dev${subApp}.jobsight.com`;
	return `https://${subApp}.jobsight.com`;
}

async function init() {
	// Remove any temp company display
	sessionStorage.removeItem('jwt_token');

	// Check to see if jwt was passed as a parameter in the url.  If so, save it in session storage
	const hash = window.location.hash;
	const queryString = hash.split('?')[1];
	let isIframe = false;

	if (queryString) {
		const params = new URLSearchParams(queryString);

		// If this is from a corporate iframe, then don't redirect to dispatch.jobsight.com even though the user will be a dispatch user
		isIframe = (params.get('isiframe') ?? 'false') === 'true';

		GlobalsService.corporateTimeZone = params.get('timezone') || 'Mountain Standard Time';

		const jwt = params.get('jwt');
		if (jwt) {
			sessionStorage.clear();
			sessionStorage.setItem('jwt_token', jwt);
		}

		const corporateCompanyId = params.get('corporateCompanyId');
		if (corporateCompanyId) {
			sessionStorage.setItem('corporate_company_id', corporateCompanyId);
			GlobalsService.corporateCompanyId = parseInt(corporateCompanyId);
		}

		const corporateUserId = params.get('corporateUserId');
		if (corporateUserId) {
			sessionStorage.setItem('corporate_user_id', corporateUserId);
			GlobalsService.corporateUserId = parseInt(corporateUserId);
		}

		if (params.get('dispatchjwt')) {
			localStorage.setItem('jwt_token', params.get('dispatchjwt'));
			const url = new URL(window.location.href);
			url.searchParams.delete('dispatchjwt');
			history.replaceState({}, '', url);
		}

		if (params.get('corporatejwt')) {
			localStorage.setItem('jwt_token', params.get('corporatejwt'));
			const url = new URL(window.location.href);
			url.searchParams.delete('corporatejwt');
			history.replaceState({}, '', url);
		}
	}

	const globalsService = inject(GlobalsService);
	const themeService = inject(ThemeService);
	const messageQueueHubService = inject(MessageQueueHubService);
	const textChatStore = inject(TextChatStore);
	const textChatHub = inject(TextChatHubService);
	const teamChatStore = inject(TeamChatStore);
	const offlineStore = inject(OfflineStore);
	const notificationSoundService = inject(NotificationSoundService);

	// The new team chat message chime.  src/content is an asset root (angular.json), so this ships
	// as-is; without it the service falls back to a synthesised tone.
	notificationSoundService.soundUrl = '/content/dingling.mp3';
	// dingling.mp3 plays at full scale otherwise, which is jarring for something that fires all day.
	notificationSoundService.volume = 0.3;

	offlineStore.init();

	localStorage.removeItem('last_action');

	const slickInitParams = new SlickInitParams();
	SlickInitService.init(slickInitParams);

	if (environment.learningpaths === true) globalsService.setLearningPaths();
	else if (environment.staging === true) globalsService.setStaging();
	else if (environment.rc === true) globalsService.setRC();
	else if (environment.hotfix === true) globalsService.setHotfix();
	else if (environment.production === false) globalsService.setDebug();

	// Admin mode comes from the host, so it's set before any of the early returns below. The forgot-password and
	// reset-password pages return early, then finish by navigating to /login in this same page load, and the
	// login screen reads isAdmin once - without it, a Global Admin account setting its password was refused.
	if (window.location.href.indexOf('pancake.jobsight.com') >= 0 || window.location.href.indexOf('learningpathspancake.jobsight.com') >= 0) {
		console.info('Set Admin', window.location.host);
		GlobalsService.isAdmin = true;
	}

	// devadmin is the local/dev admin host — only honored on a debug build so it can never
	// hand out admin in production.
	if (GlobalsService.isDebug && window.location.href.indexOf('devadmin.jobsight.com') >= 0) {
		console.info('Set Admin (debug)', window.location.host);
		GlobalsService.isAdmin = true;
	}

	if (
		window.location.host === 'payments.jobsight.com' ||
		window.location.host === 'devpayments.jobsight.com' ||
		window.location.host === 'rcpayments.jobsight.com' ||
		window.location.host === 'hotfixpayments.jobsight.com' ||
		window.location.host === 'stagingpayments.jobsight.com'
	) {
		GlobalsService.isLoggedIn = false;
		GlobalsService.isOnlinePayments = true;
		await themeService.loadTheme('default');
		return;
	}

	if (
		window.location.host === 'portal.jobsight.com' ||
		window.location.host === 'devportal.jobsight.com' ||
		window.location.host === 'rcportal.jobsight.com' ||
		window.location.host === 'hotfixportal.jobsight.com' ||
		window.location.host === 'stagingportal.jobsight.com'
	) {
		GlobalsService.isLoggedIn = false;
		GlobalsService.isClientPortal = true;
		await themeService.loadTheme('default');
		return;
	}

	const isLoggedIn = await globalsService.isLoggedIn(true);
	if (window.location.href.indexOf('/sign-estimate') >= 0) {
		GlobalsService.isLoggedIn = false;
		await themeService.loadTheme('superhero');
		return;
	}

	if (window.location.href.indexOf('/customer-forms') >= 0) {
		GlobalsService.isLoggedIn = false;
		await themeService.loadTheme('default');
		return;
	}

	if (window.location.href.indexOf('#/survey') >= 0) {
		GlobalsService.isLoggedIn = false;
		await themeService.loadTheme('default');
		return;
	}

	// The New Account Setup form is opened from an email by someone with no JobSight account yet.
	// It needs this AND the isNewAccountSetup bypass in app.component.ts — without this one the
	// login redirect below fires before routing even starts. Forced logged-out like the survey, so
	// a staff member testing their own link doesn't get the app shell (or their theme) around it.
	if (window.location.href.indexOf('#/new-account-setup') >= 0) {
		GlobalsService.isLoggedIn = false;
		await themeService.loadTheme('default');
		return;
	}

	// Unauthenticated routes that should NOT be redirected to /login.
	// These must be evaluated BEFORE the login redirect below or the redirect
	// will overwrite window.location.href and the checks here would never match.
	if (isLoggedIn === false && window.location.href.indexOf('/forgot-password') >= 0) {
		await themeService.loadTheme('superhero');
		return;
	}

	if (isLoggedIn === false && window.location.href.indexOf('/reset-password') >= 0) {
		await themeService.loadTheme('superhero');
		return;
	}

	if (isLoggedIn === false && window.location.href.indexOf('/login') <= 0) {
		window.location.href = '/#/login';
	}

	if (!isLoggedIn) {
		await themeService.loadTheme('superhero');
		return;
	}

	await globalsService.getCacheChecksums();

	await globalsService.init(false);
	await themeService.loadTheme(GlobalsService.userSettings.theme || 'superhero');

	if (!isIframe) {
		const dispatchUrl = getSubAppUrl('dispatch');
		const corporateUrl = getSubAppUrl('corporate');

		if (GlobalsService.userInfo.isDispatch && window.location.origin !== dispatchUrl) {
			await SleepService.sleep(100);
			const jwtToken = GlobalsService.jwtToken;

			GlobalsService.jwtToken = null;
			localStorage.removeItem('jwt_token');
			localStorage.removeItem('USER_INFO_CACHE_CHECKSUM');
			sessionStorage.clear();

			window.location.href = `${dispatchUrl}/#/?dispatchjwt=${jwtToken}`;
			return;
		}

		if (GlobalsService.userInfo.isCorporateAdmin && window.location.origin !== corporateUrl) {
			await SleepService.sleep(100);
			const jwtToken = GlobalsService.jwtToken;

			GlobalsService.jwtToken = null;
			localStorage.removeItem('jwt_token');
			localStorage.removeItem('USER_INFO_CACHE_CHECKSUM');
			sessionStorage.clear();

			window.location.href = `${corporateUrl}/#/?corporatejwt=${jwtToken}`;
			return;
		}
	}

	messageQueueHubService.init();

	// The text chat hub carries team chat too, so it comes up for everyone -- not just
	// companies with SMS text chat turned on.
	textChatHub.init();

	if (GlobalsService.companySettings.useTextChat) {
		await textChatStore.init(false);
	}

	if (!GlobalsService.isAdmin && !GlobalsService.userInfo.isSubcontractor && GlobalsService.userInfo.teamChatEnabled) {
		await teamChatStore.init();
	}

	// Global Admin site: only Global Admin accounts (IsGlobalAdmin, managed on the Global Admin "Users" page). Used to
	// be keyed on the user name being "admin".
	if (GlobalsService.isAdmin && isLoggedIn && !GlobalsService.userInfo.isGlobalAdmin) {
		GlobalsService.jwtToken = null;
		localStorage.removeItem('jwt_token');
		localStorage.removeItem('USER_INFO_CACHE_CHECKSUM');
		window.location.href = '/#/login';
	}

	if (!GlobalsService.isAdmin && isLoggedIn && GlobalsService.userInfo.isGlobalAdmin) {
		GlobalsService.jwtToken = null;
		sessionStorage.clear();
		localStorage.removeItem('jwt_token');
		localStorage.removeItem('USER_INFO_CACHE_CHECKSUM');
		window.location.href = '/#/login';
	}
}
